"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog"
import { Card } from "../ui/card"
import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import { Checkbox } from "../ui/checkbox"
import { Input } from "../ui/input"
import { Separator } from "../ui/separator"
import { Loader2, Building2, PauseCircle, Send, Key, MapPin, Phone, ExternalLink, Eye, EyeOff, Package, CheckCircle2, Edit, RotateCcw, ShieldAlert } from "lucide-react"
import { getSuspensionImpedimentoLabel, lineHasImpedimento } from "../../constants/suspensionOperationalReasons"
import { contractService, type ContractResponseDto } from "../../services/contracts/ContractService"
import TrackingModal from "../contracts/TrackingModal"
import type { TrackingInfoDto } from "../../types/tracking"
import contractLineService from "../../services/contracts/ContractLineService"
import { clientService } from "../../services/clients/ClientService"
import { CredentialsModal } from "./credentials-modal"
import { SuspensionProtocolsPanel } from "./suspension-protocols-panel"
import type { SuspensionLineDto, PendingToSendLineDto } from "../../types/suspensions"
import { formatCnpj, formatPhone } from "../../lib/format-utils"
import { useToast } from "../../hooks/useToast"

interface ContractSummaryModalProps {
  contractId: string | null
  open: boolean
  onClose: () => void
  onCredentialsUpdated: () => void
  onMarkSuspended: (lineId: string) => void
  onEditImpedimento?: (lineId: string) => void
  onEditImpedimentoBulk?: (lineIds: string[]) => void
  onRequestBatchSuspend?: (lineIds: string[]) => void
  onUnmarkSuspended?: (lineId: string) => void
  onBatchUpdated?: () => void
  /** Incrementar após PATCH de impedimento para recarregar linhas do contrato. */
  reloadKey?: number
}

export function ContractSummaryModal({
  contractId,
  open,
  onClose,
  onCredentialsUpdated,
  onMarkSuspended,
  onEditImpedimento,
  onEditImpedimentoBulk,
  onRequestBatchSuspend,
  onUnmarkSuspended,
  onBatchUpdated,
  reloadKey = 0,
}: ContractSummaryModalProps) {
  const [contractData, setContractData] = useState<ContractResponseDto | null>(null)
  const [allContractLines, setAllContractLines] = useState<any[]>([])
  const [contractSuspensions, setContractSuspensions] = useState<SuspensionLineDto[]>([])
  const [contractToSend, setContractToSend] = useState<PendingToSendLineDto[]>([])
  const [loading, setLoading] = useState(false)
  const [secondaryLoading, setSecondaryLoading] = useState(false)
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false)
  const [clientIdForCredentials, setClientIdForCredentials] = useState<string | null>(null)
  const [credentials, setCredentials] = useState<{ meuTimLogin: string | null; meuTimSenha: string | null } | null>(null)
  const [trackingCode, setTrackingCode] = useState<string>("")
  const [trackingTimelineOpen, setTrackingTimelineOpen] = useState(false)
  const [trackingTimelineInfo, setTrackingTimelineInfo] = useState<TrackingInfoDto | null>(null)
  const [trackingTimelineLoading, setTrackingTimelineLoading] = useState(false)
  const [trackingTimelineError, setTrackingTimelineError] = useState<string | null>(null)
  const [trackingModalSaveLoading, setTrackingModalSaveLoading] = useState(false)
  const [trackingModalSaveError, setTrackingModalSaveError] = useState<string | null>(null)
  const [isMarkingAsSent, setIsMarkingAsSent] = useState(false)
  const [selectedSuspensionLineIds, setSelectedSuspensionLineIds] = useState<Set<string>>(new Set())
  const [isBatchProcessing, setIsBatchProcessing] = useState(false)
  const { showSuccess, showError } = useToast()
  const [protocoloModalOpen, setProtocoloModalOpen] = useState(false)
  const [isViewingCredentials, setIsViewingCredentials] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    if (open && contractId) {
      loadContractData()
    } else {
      // Limpar dados ao fechar
      setContractData(null)
      setAllContractLines([])
      setContractSuspensions([])
      setContractToSend([])
      setIsViewingCredentials(false)
      setShowPassword(false)
      setSelectedSuspensionLineIds(new Set())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractId, reloadKey])

  const loadContractSecondaryData = async (targetContractId: string) => {
    setSecondaryLoading(true)
    try {
      const [pendingSuspensions, pendingToSend] = await Promise.all([
        contractLineService.getPendingSuspensions({ contractId: targetContractId }),
        contractLineService.getPendingToSend({ contractId: targetContractId }),
      ])
      setContractSuspensions(pendingSuspensions)
      setContractToSend(pendingToSend)
    } catch (error) {
      console.error("Erro ao carregar pendências do contrato:", error)
      setContractSuspensions([])
      setContractToSend([])
    } finally {
      setSecondaryLoading(false)
    }
  }

  const loadCredentialsIfNeeded = async (clientId: string) => {
    try {
      const creds = await clientService.getClientMeuTimCredentials(clientId)
      const login = creds.meuTimLogin || ""
      const senha = creds.meuTimSenha || ""
      if (login.trim() && senha.trim()) {
        setCredentials({ meuTimLogin: login, meuTimSenha: senha })
      } else {
        setCredentials(null)
      }
    } catch {
      setCredentials(null)
    }
  }

  const loadContractData = async () => {
    if (!contractId) return
    try {
      setLoading(true)
      setCredentials(null)
      setContractSuspensions([])
      setContractToSend([])

      const contract = await contractService.getContractById(contractId)
      const allLines = await contractService.getContractLines(contractId)

      setContractData(contract)
      setAllContractLines(allLines)
      setSelectedSuspensionLineIds(new Set())
      setTrackingCode(contract.trackingCode || "")
    } catch (error) {
      console.error("Erro ao carregar dados do contrato:", error)
    } finally {
      setLoading(false)
    }

    if (contractId) {
      void loadContractSecondaryData(contractId)
    }
  }

  // Lógica de detecção dinâmica
  const hasSuspensions = contractSuspensions.length > 0
  const hasToSend = contractToSend.length > 0
  const pendingSuspensionLines = contractSuspensions.filter((line) => !line.estaSuspensa)
  const selectedPendingLineIds = Array.from(selectedSuspensionLineIds).filter((lineId) =>
    pendingSuspensionLines.some((line) => line.id === lineId)
  )

  const contractType: "suspensions" | "toSend" | "both" =
    hasSuspensions && hasToSend ? "both" : hasSuspensions ? "suspensions" : "toSend"

  // Título dinâmico do modal
  const modalTitle = secondaryLoading
    ? "Resumo do Contrato"
    : contractType === "both"
      ? "Resumo do Contrato - Completo"
      : contractType === "suspensions"
        ? "Resumo do Contrato - Suspensões"
        : hasToSend
          ? "Resumo do Contrato - Envios"
          : "Resumo do Contrato"

  const firstSuspensionLine = contractSuspensions[0]
  const hasCredentials =
    Boolean(credentials?.meuTimLogin?.trim() && credentials?.meuTimSenha?.trim()) ||
    contractSuspensions.some((s) => s.temCredenciais)

  const handleEditCredentials = () => {
    if (contractData?.clientId) {
      setClientIdForCredentials(contractData.clientId)
      setCredentialsModalOpen(true)
      setIsViewingCredentials(false)
    }
  }

  const handleViewCredentials = () => {
    setIsViewingCredentials(true)
    if (contractData?.clientId && !credentials) {
      void loadCredentialsIfNeeded(contractData.clientId)
    }
  }

  const handleViewProtocolo = () => {
    if (firstSuspensionLine) {
      window.open(`/protocolos/${firstSuspensionLine.protocolo}`, "_blank")
      setProtocoloModalOpen(true)
    }
  }

  const handleCloseProtocoloModal = () => {
    setProtocoloModalOpen(false)
  }

  const handleCredentialsUpdated = async () => {
    // Recarregar dados do contrato para mostrar credenciais atualizadas
    await loadContractData()
    // Chamar callback pai para atualizar lista principal
    onCredentialsUpdated()
  }

  const handleViewContract = () => {
    if (contractId) {
      const cnpj = contractData?.client?.cnpj
      const params = new URLSearchParams()
      params.append('highlight', contractId)
      if (cnpj) {
        // Remover formatação do CNPJ para o filtro
        const cleanCnpj = cnpj.replace(/[./-]/g, '')
        params.append('clientCnpj', cleanCnpj)
      }
      window.open(`/contracts?${params.toString()}`, "_blank")
    }
  }

  const handleUpdateTrackingCode = async () => {
    if (!contractId) return
    try {
      await contractService.updateTrackingCode(contractId, trackingCode)
      showSuccess("Código de rastreio atualizado com sucesso")
      await loadContractData()
    } catch (error: any) {
      showError(error.message || "Erro ao atualizar código de rastreio")
    }
  }

  const handleSaveTrackingFromModal = async (code: string) => {
    if (!contractId) return
    setTrackingModalSaveLoading(true)
    setTrackingModalSaveError(null)
    try {
      await contractService.updateTrackingCode(contractId, code)
      const trimmed = code.trim()
      setTrackingCode(trimmed)
      setContractData((prev) => (prev ? { ...prev, trackingCode: trimmed } : null))
      setTrackingTimelineError(null)
      setTrackingTimelineLoading(true)
      try {
        const info = await contractService.getContractTracking(contractId)
        setTrackingTimelineInfo(info)
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } }; message?: string }
        setTrackingTimelineError(
          err?.response?.data?.message ?? err?.message ?? "Erro ao consultar rastreamento"
        )
      } finally {
        setTrackingTimelineLoading(false)
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string }
      const msg = err?.response?.data?.message ?? err?.message ?? "Erro ao salvar código de rastreio"
      setTrackingModalSaveError(msg)
      throw e
    } finally {
      setTrackingModalSaveLoading(false)
    }
  }

  const handleVerRastro = async () => {
    if (!contractId || !trackingCode.trim()) return
    setTrackingTimelineOpen(true)
    setTrackingModalSaveError(null)
    setTrackingTimelineInfo(null)
    setTrackingTimelineError(null)
    setTrackingTimelineLoading(true)
    try {
      const info = await contractService.getContractTracking(contractId)
      setTrackingTimelineInfo(info)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string }
      setTrackingTimelineError(
        err?.response?.data?.message ?? err?.message ?? "Erro ao consultar rastreamento"
      )
    } finally {
      setTrackingTimelineLoading(false)
    }
  }

  const handleCloseTrackingTimeline = () => {
    setTrackingTimelineOpen(false)
    setTrackingTimelineInfo(null)
    setTrackingTimelineError(null)
    setTrackingTimelineLoading(false)
    setTrackingModalSaveError(null)
    setTrackingModalSaveLoading(false)
  }

  const handleMarkAsSent = async () => {
    if (!contractId) return
    try {
      setIsMarkingAsSent(true)
      await contractService.markContractAsSent(contractId, trackingCode || undefined)
      showSuccess("Contrato marcado como enviado com sucesso")
      await loadContractData()
      onClose()
    } catch (error: any) {
      showError(error.message || "Erro ao marcar contrato como enviado")
    } finally {
      setIsMarkingAsSent(false)
    }
  }

  const toggleSuspensionLineSelection = (lineId: string, checked: boolean) => {
    setSelectedSuspensionLineIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(lineId)
      else next.delete(lineId)
      return next
    })
  }

  const selectAllPendingSuspensions = () => {
    setSelectedSuspensionLineIds(new Set(pendingSuspensionLines.map((line) => line.id)))
  }

  const runBatchOperation = async (operation: "suspend" | "cancel", lineIds: string[]) => {
    if (lineIds.length === 0) {
      showError("Selecione ao menos uma linha pendente.")
      return
    }
    try {
      setIsBatchProcessing(true)
      if (operation === "suspend" && onRequestBatchSuspend) {
        onRequestBatchSuspend(lineIds)
        return
      }

      const result =
        operation === "suspend"
          ? await contractLineService.markAsSuspendedBatch(lineIds)
          : await contractLineService.cancelSuspensionBatch(lineIds)
      showSuccess(
        operation === "suspend"
          ? `${result.updated} linha(s) marcada(s) como suspensa(s).`
          : `${result.updated} solicitação(ões) cancelada(s).`
      )
      await loadContractData()
      onBatchUpdated?.()
    } catch (error: any) {
      showError(error.message || "Erro ao processar linhas selecionadas")
    } finally {
      setIsBatchProcessing(false)
    }
  }

  if (!open) return null

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {modalTitle}
            </DialogTitle>
            <DialogDescription>Informações completas do contrato e suas linhas</DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : contractData ? (
            <div className="space-y-6 py-4">
              {/* Seção 1: Informações do Cliente (Sempre visível) */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-semibold text-muted-foreground">Cliente</span>
                </div>
                <p className="font-semibold text-lg">{contractData.client?.razaoSocial || "N/A"}</p>
                <p className="text-sm text-muted-foreground">
                  CNPJ: {formatCnpj(contractData.client?.cnpj || "")}
                </p>
                <p className="text-sm text-muted-foreground">
                  Contrato: {contractData.id?.substring(0, 8).toUpperCase()}
                </p>
              </div>

              <Separator />

              {/* Seção 2: Estatísticas (Dinâmica) */}
              {contractType === "both" ? (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <p className="text-2xl font-bold">{allContractLines.length}</p>
                    <p className="text-xs text-muted-foreground">Total de Linhas</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-2xl font-bold text-orange-600">
                      {secondaryLoading ? "…" : contractSuspensions.length}
                    </p>
                    <p className="text-xs text-muted-foreground">A Suspender</p>
                  </div>
                  <div className="text-center p-3 bg-violet-50 rounded-lg">
                    <p className="text-2xl font-bold text-violet-600">
                      {secondaryLoading ? "…" : contractToSend.length}
                    </p>
                    <p className="text-xs text-muted-foreground">A Enviar</p>
                  </div>
                </div>
              ) : contractType === "suspensions" ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <p className="text-2xl font-bold">{allContractLines.length}</p>
                    <p className="text-xs text-muted-foreground">Total de Linhas</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-2xl font-bold text-orange-600">
                      {secondaryLoading ? "…" : contractSuspensions.length}
                    </p>
                    <p className="text-xs text-muted-foreground">A Suspender</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <p className="text-2xl font-bold">{allContractLines.length}</p>
                    <p className="text-xs text-muted-foreground">Total de Linhas</p>
                  </div>
                  <div className="text-center p-3 bg-violet-50 rounded-lg">
                    <p className="text-2xl font-bold text-violet-600">
                      {secondaryLoading ? "…" : contractToSend.length}
                    </p>
                    <p className="text-xs text-muted-foreground">A Enviar</p>
                  </div>
                </div>
              )}

              {secondaryLoading && !hasSuspensions && !hasToSend && (
                <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando pendências…
                </div>
              )}

              {/* Seção 3: Linhas a Suspender (Apenas se hasSuspensions) */}
              {hasSuspensions && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <h3 className="font-semibold flex items-center gap-2">
                      <PauseCircle className="h-4 w-4" />
                      Linhas a marcar como suspensas ({contractSuspensions.length})
                    </h3>
                    {contractId && (
                      <SuspensionProtocolsPanel
                        contractId={contractId}
                        lines={contractSuspensions.map((l) => ({
                          id: l.id,
                          ddd: l.ddd,
                          numero: l.numero,
                        }))}
                        reloadKey={reloadKey}
                        onUpdated={onBatchUpdated}
                      />
                    )}
                    <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 p-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={selectAllPendingSuspensions}
                        disabled={isBatchProcessing || pendingSuspensionLines.length === 0}
                      >
                        Selecionar todas pendentes
                      </Button>
                      {onEditImpedimentoBulk && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-violet-300 text-violet-700"
                          onClick={() => onEditImpedimentoBulk(selectedPendingLineIds)}
                          disabled={isBatchProcessing || selectedPendingLineIds.length === 0}
                        >
                          <ShieldAlert className="h-4 w-4 mr-2" />
                          Registar impedimento
                          {selectedPendingLineIds.length > 0
                            ? ` (${selectedPendingLineIds.length})`
                            : ""}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => runBatchOperation("suspend", selectedPendingLineIds)}
                        disabled={isBatchProcessing || selectedPendingLineIds.length === 0}
                      >
                        {isBatchProcessing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                        Marcar como Suspenso (selecionadas)
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => runBatchOperation("suspend", pendingSuspensionLines.map((line) => line.id))}
                        disabled={isBatchProcessing || pendingSuspensionLines.length === 0}
                      >
                        Marcar como Suspenso (todas pendentes)
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-orange-700 border-orange-300"
                        onClick={() => runBatchOperation("cancel", selectedPendingLineIds)}
                        disabled={isBatchProcessing || selectedPendingLineIds.length === 0}
                      >
                        Cancelar suspensão selecionadas
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        {selectedPendingLineIds.length} selecionada(s)
                      </span>
                    </div>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {contractSuspensions.map((line) => (
                        <Card key={line.id} className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              <Checkbox
                                checked={selectedSuspensionLineIds.has(line.id)}
                                disabled={line.estaSuspensa || isBatchProcessing}
                                onCheckedChange={(checked) => toggleSuspensionLineSelection(line.id, checked === true)}
                                aria-label={`Selecionar linha ${line.ddd}${line.numero || ""}`}
                              />
                              <div className="flex-1">
                              <p className="font-medium">{formatPhone(line.ddd, line.numero)}</p>
                              <p className="text-sm text-muted-foreground">{line.planName}</p>
                              {line.protocolo && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  <span className="font-medium">Último protocolo:</span>{" "}
                                  <span className="font-mono">{line.protocolo}</span>
                                </p>
                              )}
                              {line.diasRestantes !== null && (
                                <Badge
                                  className={
                                    line.diasRestantes <= 3
                                      ? "bg-red-100 text-red-700 mt-1"
                                      : line.diasRestantes <= 7
                                        ? "bg-orange-100 text-orange-700 mt-1"
                                        : "bg-yellow-100 text-yellow-700 mt-1"
                                  }
                                >
                                  {line.diasRestantes} dias restantes
                                </Badge>
                              )}
                              {line.dataSuspensao === null && !lineHasImpedimento(line) && (
                                <Badge className="bg-fuchsia-100 text-fuchsia-700 mt-1 ml-1">
                                  1ª Suspensão
                                </Badge>
                              )}
                              {lineHasImpedimento(line) && (
                                <Badge className="bg-violet-100 text-violet-800 border-violet-300 mt-1 ml-1">
                                  Com impedimento
                                </Badge>
                              )}
                              {!line.estaSuspensa &&
                                getSuspensionImpedimentoLabel(
                                  line.motivoOperacional,
                                  line.motivoOperacionalLabel
                                ) && (
                                  <p className="text-xs text-violet-700 mt-1 font-medium">
                                    Impedimento:{" "}
                                    {getSuspensionImpedimentoLabel(
                                      line.motivoOperacional,
                                      line.motivoOperacionalLabel
                                    )}
                                  </p>
                                )}
                              {line.estaSuspensa && (
                                <Badge className="bg-emerald-100 text-emerald-700 mt-1 ml-1">
                                  Suspensa
                                </Badge>
                              )}
                              {line.dataSuspensao && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Suspensa em {new Date(line.dataSuspensao).toLocaleDateString("pt-BR")}
                                  {line.dataProximaSuspensao
                                    ? ` · próxima em ${new Date(line.dataProximaSuspensao).toLocaleDateString("pt-BR")}`
                                    : ""}
                                </p>
                              )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {line.estaSuspensa && onUnmarkSuspended ? (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => {
                                    if (window.confirm('Tem certeza que deseja reverter a suspensão desta linha?')) {
                                      onUnmarkSuspended(line.id)
                                    }
                                  }}
                                >
                                  <RotateCcw className="h-4 w-4 mr-1" />
                                  Reverter
                                </Button>
                              ) : (
                                <>
                                  {onEditImpedimento && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-violet-300 text-violet-700"
                                      onClick={() => onEditImpedimento(line.id)}
                                    >
                                      <ShieldAlert className="h-4 w-4 mr-1" />
                                      Impedimento
                                    </Button>
                                  )}
                                  <Button size="sm" onClick={() => onMarkSuspended(line.id)}>
                                    Marcar como Suspenso
                                  </Button>
                                </>
                              )}
                            </div>
                            </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Seção 4: Credenciais (Apenas se hasSuspensions) */}
              {hasSuspensions && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Key className="h-4 w-4" />
                      Credenciais Meu TIM
                    </h3>
                    {hasCredentials && credentials ? (
                      isViewingCredentials ? (
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-sm mb-1">
                            <span className="font-medium">Login:</span> {credentials.meuTimLogin || "N/A"}
                          </p>
                          <div className="text-sm mb-3 flex items-center gap-2">
                            <span className="font-medium">Senha:</span>
                            <span className="flex-1 font-mono">
                              {showPassword ? (credentials.meuTimSenha || "N/A") : ("*".repeat(credentials.meuTimSenha?.length || 0) || "N/A")}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => setShowPassword(!showPassword)}
                              title={showPassword ? "Ocultar senha" : "Mostrar senha"}
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={handleEditCredentials}>
                              Editar
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => {
                              setIsViewingCredentials(false)
                              setShowPassword(false)
                            }}>
                              Fechar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-sm text-green-700 mb-2">Credenciais configuradas</p>
                          <Button size="sm" variant="outline" onClick={handleViewCredentials}>
                            <Eye className="h-4 w-4 mr-2" />
                            Visualizar Credenciais
                          </Button>
                        </div>
                      )
                    ) : (
                      <div className="p-3 bg-amber-50 rounded-lg">
                        <p className="text-sm text-amber-700 mb-2">Credenciais não configuradas</p>
                        <Button size="sm" onClick={handleEditCredentials}>
                          Adicionar Credenciais
                        </Button>
                      </div>
                    )}
                    {firstSuspensionLine?.protocolo && (
                      <p className="text-sm mb-1">
                        <span className="font-medium">Protocolo:</span> {firstSuspensionLine.protocolo}
                        <Button size="sm" variant="outline" onClick={handleViewProtocolo}>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Ver Protocolo
                        </Button>
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Seção 5: Linhas a Enviar (Apenas se hasToSend) */}
              {hasToSend && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      Linhas a Enviar ({contractToSend.length})
                    </h3>
                    
                    {/* Campo de código de rastreio */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Código de Rastreio
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <Input
                          value={trackingCode}
                          onChange={(e) => setTrackingCode(e.target.value)}
                          placeholder="Ex: BR123456789BR"
                          className="flex-1 min-w-[200px]"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleUpdateTrackingCode}
                          disabled={isMarkingAsSent}
                        >
                          Salvar
                        </Button>
                        {trackingCode.trim() ? (
                          <Button size="sm" variant="secondary" onClick={handleVerRastro} disabled={isMarkingAsSent}>
                            Ver rastro
                          </Button>
                        ) : null}
                      </div>
                      {contractData?.dataEnvio && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span>Enviado em {new Date(contractData.dataEnvio).toLocaleDateString('pt-BR')}</span>
                        </div>
                      )}
                    </div>

                    {/* Botão Marcar como Enviado */}
                    {!contractData?.dataEnvio && (
                      <Button
                        onClick={handleMarkAsSent}
                        disabled={isMarkingAsSent}
                        className="w-full"
                        variant="default"
                      >
                        {isMarkingAsSent ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Marcando...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Marcar como Enviado
                          </>
                        )}
                      </Button>
                    )}
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {contractToSend.map((line) => (
                        <Card key={line.id} className="p-3">
                          <div className="space-y-2">
                            <div>
                              <p className="font-medium">{formatPhone(line.ddd, line.numero)}</p>
                              <p className="text-sm text-muted-foreground">{line.planName}</p>
                              {line.chipCcid && (
                                <Badge className="bg-green-100 text-green-700 mt-1">
                                  ICCID: {line.chipCcid}
                                </Badge>
                              )}
                            </div>
                            {line.deliveryAddress ? (
                              <div className="p-2 bg-blue-50 rounded text-sm">
                                <p className="font-medium mb-1 flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  Endereço:
                                </p>
                                <p>
                                  {line.deliveryAddress.endereco}, {line.deliveryAddress.numero}
                                </p>
                                {line.deliveryAddress.complemento && (
                                  <p>{line.deliveryAddress.complemento}</p>
                                )}
                                <p>
                                  {line.deliveryAddress.bairro} - {line.deliveryAddress.cidade}/
                                  {line.deliveryAddress.estado}
                                </p>
                                <p>CEP: {line.deliveryAddress.cep}</p>
                                {line.deliveryAddress.referencia && (
                                  <p className="mt-1 text-xs text-muted-foreground">
                                    Ref: {line.deliveryAddress.referencia}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div className="p-2 bg-amber-50 rounded text-sm text-amber-700">
                                ⚠️ Endereço não informado
                              </div>
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : null}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={onClose}>
              Fechar
            </Button>
            {contractId && (
              <Button variant="outline" onClick={handleViewContract}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Ver Contrato Completo
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <TrackingModal
        open={trackingTimelineOpen}
        contractId={contractId}
        trackingCode={(contractData?.trackingCode ?? trackingCode).trim()}
        canEditCode
        trackingInfo={trackingTimelineInfo}
        loading={trackingTimelineLoading}
        fetchError={trackingTimelineError}
        saveLoading={trackingModalSaveLoading}
        saveError={trackingModalSaveError}
        onSaveCode={handleSaveTrackingFromModal}
        onClose={handleCloseTrackingTimeline}
      />

      {/* CredentialsModal integrado */}
      <CredentialsModal
        lineId={null}
        clientIdForCredentials={clientIdForCredentials}
        open={credentialsModalOpen}
        onClose={() => {
          setCredentialsModalOpen(false)
          setClientIdForCredentials(null)
        }}
        onCredentialsUpdated={handleCredentialsUpdated}
      />
    </>
  )
}

